<template>
  <div class="report-selector-container">
    <div class="selector-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <p class="subtitle">{{ pageSubtitle }}</p>
    </div>

    <!-- Selectors Card -->
    <v-card class="selector-card" elevation="0">
      <v-card-text>
        <div class="selectors-grid">
          <!-- Workspace Select -->
          <div class="selector-wrapper">
            <label class="selector-label">Workspace</label>
            <div class="custom-select-wrapper">
              <select
                v-model="selectedWorkspaceId"
                @change="onWorkspaceChange"
                :disabled="loadingWorkspaces"
                class="native-select"
              >
                <option value="">Select a workspace</option>
                <option v-for="ws in workspaces" :key="ws.value" :value="ws.value">
                  {{ ws.name }}
                </option>
              </select>
              <v-icon class="select-icon">mdi-folder</v-icon>
            </div>
          </div>

          <!-- Report Select -->
          <div class="selector-wrapper">
            <label class="selector-label">Report</label>
            <div class="custom-select-wrapper">
              <select
                v-model="selectedReportId"
                @change="onReportChange"
                :disabled="!selectedWorkspaceId || loadingReports"
                class="native-select"
              >
                <option value="">Select a report</option>
                <option v-for="report in reports" :key="report.value" :value="report.value">
                  {{ report.name }}
                </option>
              </select>
              <v-icon class="select-icon">mdi-chart-box-outline</v-icon>
            </div>
          </div>
        </div>

        <!-- Refresh & History Buttons -->
        <div class="header-action-buttons">
          <v-btn
            color="primary"
            class="mr-3"
            @click="onRefresh"
            :loading="loadingWorkspaces || loadingReports"
            :disabled="!selectedWorkspaceId || !selectedReportId"
          >
            Refresh
          </v-btn>

          <v-btn
            color="primary"
            class="mr-3"
            @click="onHistory"
            :loading="loadingHistory"
            :disabled="!selectedWorkspaceId || !selectedReportId"
          >
            Refresh History
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

   <!-- Refresh History List -->
<v-card v-if="refreshHistory.length > 0" class="history-card" elevation="2">
  <v-card-title class="history-title">
    <v-icon start color="primary">mdi-history</v-icon>
    Refresh History
  </v-card-title>

  <v-card-text class="pa-0">
    <v-list lines="one" class="py-0">
      <v-list-item
        v-for="(item, index) in refreshHistory"
        :key="item.requestId || index"
        class="history-item px-6">
        <template #prepend>
          <v-icon
            size="large"
            :color="item.status === 'Completed' ? 'success' : item.status === 'Failed' || item.status === 'Cancelled' ? 'error' : 'warning'"
          >
            {{ item.status === 'Completed' ? 'mdi-check-circle' : 
               item.status === 'Failed' || item.status === 'Cancelled' ? 'mdi-alert-circle' : 
               'mdi-clock-outline' }}
          </v-icon>
        </template>

        <v-list-item-title class="font-weight-medium mb-1">
          {{ formatDate(item.startTime) }}
          <span class="text-medium-emphasis">→</span>
          {{ item.endTime ? formatDate(item.endTime) : 'In progress' }}
        </v-list-item-title>

        <v-list-item-subtitle class="d-flex flex-wrap align-center gap-3">
          <div>
            Status:
            <v-chip
              :color="getStatusColor(item.status)"
              size="small"
              class="font-weight-medium ml-1"
            >
              {{ item.status === "Unknown" ? "In Progress" : item.status}}
            </v-chip>
          </div>
          <div class="text-medium-emphasis">
            Duration: <strong>{{ calculateDuration(item.startTime, item.endTime) }}</strong>
          </div>
          <div v-if="item.refreshType" class="text-caption text-disabled">
            {{ item.refreshType}}
          </div>
          <!-- Cancel button - visible ONLY when status is Unknown (In Progress) -->
        <template>
          <v-btn
            v-if='item.status === "Unknown"'
            icon
            size="small"
            color="error"
            variant="text"
            @click="cancelRefresh(item)"
            title="Cancel refresh"
          >
            <v-icon>mdi-cancel</v-icon>
          </v-btn>
        </template>
        </v-list-item-subtitle>
      </v-list-item>
    </v-list>
  </v-card-text>
</v-card>

<!-- Empty state -->
<v-card v-else-if="historyFetched && refreshHistory.length === 0" class="history-card empty-history" elevation="2">
  <v-card-text class="text-center py-12">
    <v-icon size="64" color="grey lighten-2" class="mb-4">mdi-history</v-icon>
    <div class="text-h6 grey--text text--darken-2 mb-2">No refresh history available</div>
    <div class="text-body-2 grey--text">Try triggering a refresh first.</div>
  </v-card-text>
</v-card>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, defineProps, defineEmits } from "vue";
import ApiService from "@/services/apiService";
import { logInfo, logError } from "@/utils";
import * as pbi from "powerbi-client";

// Props
const props = defineProps({
  currentRequester: {
    type: String,
    required: true,
    validator: (value) => ['interno', 'prod', 'test'].includes(value)
  },
  pageTitle: {
    type: String,
    default: 'PBI Report Refresh'
  },
  pageSubtitle: {
    type: String,
    default: 'Select workspace and reports'
  },
  brandLabel: {
    type: String,
    default: 'Brand'
  },
  filterConfig: {
    type: Object,
    default: () => ({
      table: 'surveys',
      column: 'idApp'
    })
  },
  embedSettings: {
    type: Object,
    default: () => ({
      background: pbi.models.BackgroundType.Transparent,
      localeSettings: { language: 'es' },
      panes: {
        filters: { expanded: false, visible: false },
        pageNavigation: { visible: true }
      }
    })
  }
});

// Emits
const emit = defineEmits(['workspace-changed', 'report-changed', 'brand-changed', 'report-loaded']);

// PowerBI Service
const powerbi = new pbi.service.Service(
  pbi.factories.hpmFactory,
  pbi.factories.wpmpFactory,
  pbi.factories.routerFactory
);

// State
const workspaces = ref([]);
const reports = ref([]);
const brands = ref([]);
const selectedWorkspaceId = ref("");
const selectedReportId = ref("");
const selectedReportDatasetId = ref("")
const selectedAppId = ref("");
const loadingWorkspaces = ref(false);
const loadingReports = ref(false);
const embedConfig = ref(null);
const reportContainer = ref(null);
let embeddedReport = null;

const loadingHistory = ref(false);
const refreshHistory = ref([]);
const historyFetched = ref(false);

// Computed
const selectedWorkspace = computed(() => {
  return workspaces.value.find(ws => ws.value === selectedWorkspaceId.value) || null;
});

const selectedReport = computed(() => {
  return reports.value.find(r => r.value === selectedReportId.value) || null;
});

// Methods
const getToken = async () => {
  try {
    loadingWorkspaces.value = true;
    
    const tokenRes = await ApiService.get("/powerbi/generate-token");
    if (!tokenRes.success) {
      throw new Error("Failed to generate Power BI token");
    }

    const workspaceRes = await ApiService.get("/powerbi/get-workspaces");
    if (!workspaceRes.success) {
      throw new Error("Failed to load workspaces");
    }

    workspaces.value = workspaceRes.data || [];
  } catch (error) {
    console.error("Error loading workspaces:", error);
    alert(error.message || "Error loading data");
  } finally {
    loadingWorkspaces.value = false;
  }
};

const getActiveWorkspace = async() => {
  try{
    const activeWorkspace = await ApiService.get("/powerbi/get-active");

    if(activeWorkspace.status === 200 && activeWorkspace.success) {
        selectedWorkspaceId.value = activeWorkspace?.data?.[0]?.workspace_id || "";
        selectedReportDatasetId.value = activeWorkspace?.data?.[0]?.dataset_id || "";
        selectedReportId.value = activeWorkspace?.data?.[0]?.report_id || ""

        const reportResponse = await ApiService.post("/powerbi/get-reports", {
        workspaceId: selectedWorkspaceId.value
        });

        if (reportResponse.success) {
          reports.value = reportResponse.data || [];
        }
    }
  }catch(error) {
    console.log("Error: ", error);
    alert('algo salió mal');
  }
}

const onWorkspaceChange = async () => {
  // Reset downstream selections
  selectedReportId.value = "";
  selectedAppId.value = "";
  reports.value = [];
  brands.value = [];
  resetEmbed();

  if (!selectedWorkspaceId.value) return;

  loadingReports.value = true;
  try {
    const reportResponse = await ApiService.post("/powerbi/get-reports", {
      workspaceId: selectedWorkspaceId.value
    });

    if (reportResponse.success) {
      reports.value = reportResponse.data || [];

      emit('workspace-changed', {
        workspaceId: selectedWorkspaceId.value,
        workspace: selectedWorkspace.value
      });
    }
  } catch (error) {
    console.error("Error fetching reports:", error);
    alert("Failed to load reports");
  } finally {
    loadingReports.value = false;
  }
};

const onReportChange = async () => {
  // Reset downstream selections
  selectedAppId.value = "";
  brands.value = [];
  resetEmbed();

  if (!selectedReportId.value) return;

  try {
    const selectedReport = reports.value.find(r => r.value === selectedReportId.value);
    if (!selectedReport) return;

    selectedReportId.value = selectedReport.value;
    selectedReportDatasetId.value = selectedReport.datasetId;

  } catch (error) {
    console.error("Error handling report change:", error);
    alert(error.message || "Failed to load report data");
  }
};

const resetEmbed = () => {
  if (reportContainer.value && embeddedReport) {
    powerbi.reset(reportContainer.value);
    embeddedReport = null;
  }
  embedConfig.value = null;
};

const onHistory = async () => {
  if (!selectedWorkspaceId.value || !selectedReportDatasetId.value) return;

  loadingHistory.value = true;
  historyFetched.value = true;
  try {
    const historyResponse = await ApiService.post("/powerbi/get-refresh-history", {
      workspaceId: selectedWorkspaceId.value,
      datasetId: selectedReportDatasetId.value 
    });

    if (historyResponse.success) {
      refreshHistory.value = historyResponse.data || [];
    } else {
      refreshHistory.value = [];
      logError("Failed to fetch refresh history");
    }
  } catch (error) {
    console.error("Error fetching refresh history:", error);
    refreshHistory.value = [];
    logError("Error loading refresh history");
  } finally {
    loadingHistory.value = false;
  }
};


const onRefresh = async() => {
   const refreshResponse = await ApiService.post("/powerbi/start-sync", {
    "force_sync": false,
    "run_async": false
    });


    if(refreshResponse.success) {
      logInfo("Refresh process started")
      setTimeout(async() => {
        await onHistory();
      }, 2000)
    }
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const calculateDuration = (start, end) => {
  if (!start) return '-';
  if (!end) return 'In progress';

  const startDate = new Date(start);
  const endDate = new Date(end);
  const diff = endDate - startDate;

  const minutes = Math.floor(diff / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);

  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

const getStatusColor = (status) => {
  switch (status) {
    case 'Completed':
      return 'success';
    case 'Failed':
    case 'Cancelled':
      return 'error';
    case 'InProgress':
    case 'Unknown':
    default:
      return 'warning';
  }
};

const cancelRefresh = async(item) => {
  const payload = {
    refreshId: item.requestId,
    workspaceId: selectedWorkspaceId.value,
    datasetId: selectedReportDatasetId.value
  }

  try{
   const cancelResponse = await ApiService.post("/powerbi/cancel-request", payload);


    if(cancelResponse.success) {
      logInfo("Request Cancelled")
      return  await onHistory();
    }
  }catch(error) {
    logError("Something went wrong");
  }
}


// Keep your onMounted
onMounted(() => {
  getToken();
  getActiveWorkspace();
});
</script>

<style scoped>
.report-selector-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f5f7 0%, #efefef 100%);
  padding: 2rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif;
}

.selector-header {
  margin-bottom: 3rem;
  text-align: center;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.subtitle {
  font-size: 1rem;
  color: #666;
}

.selector-card {
  background: white;
  border-radius: 12px;
  margin-bottom: 2rem;
  border: 1px solid #e5e5e5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.selectors-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 2rem;
}

@media (max-width: 768px) {
  .selectors-grid {
    grid-template-columns: 1fr;
  }
}

.selector-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.selector-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a1a1a;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.custom-select-wrapper {
  position: relative;
}

.native-select {
  width: 100%;
  padding: 0.75rem 2.5rem 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background-color: white;
  appearance: none;
  cursor: pointer;
  transition: all 0.2s;
}

.native-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.native-select:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
  opacity: 0.6;
}

.select-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #3b82f6;
  pointer-events: none;
  font-size: 1.25rem;
}

.header-action-buttons {
  display: flex;
  justify-content: flex-start;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e5e5e5;
}

/* === Refresh History Styles === */
.history-card {
  background: white;
  border-radius: 12px;
  margin-top: 2rem;
  border: 1px solid #e5e5e5;
  overflow: hidden;
}

.history-title {
  font-size: 1.35rem;
  font-weight: 600;
  color: #1a1a1a;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 1rem 1.5rem;
}

.history-item {
  min-height: 76px;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.2s;
}

.history-item:hover {
  background: #f9fafb;
}

.history-item:last-child {
  border-bottom: none;
}

.empty-history {
  text-align: center;
  border: 2px dashed #cbd5e1;
  background: #fbfcfd;
}

.gap-3 {
  gap: 1rem;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .history-item {
    flex-direction: column;
    align-items: flex-start !important;
    text-align: left;
    padding: 1rem;
  }

  .history-item .v-list-item__prepend {
    margin-right: 0;
    margin-bottom: 0.5rem;
  }

  .v-list-item-subtitle .gap-3 {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}

/* Keep your other embed styles if needed */
.embed-wrapper { margin-top: 2.5rem; animation: fadeInUp 0.6s ease-out; }
@keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
</style>